import { Button, Tooltip } from '@nextui-org/react';
import { useRef, useState } from 'react';
import { CheckmarkIcon } from 'react-hot-toast';
import { cn } from '@/lib/utils';

interface CopyButtonProps {
  value: string;
  onClick?: (e: React.MouseEvent) => void;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

const CopyButton = ({ value, onClick, className, size = 'sm' }: CopyButtonProps) => {
  const widgetCopyButtonIcon = useRef<HTMLButtonElement | null>(null);
  const [textCopiedToClipboard, setTextCopiedToClipboard] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const onCopyWidgetBlur = () => {
    setTimeout(() => {
      setTextCopiedToClipboard(false);
      setIsOpen(false);
    }, 750);
  };

  const copyTextToClipboard = () => {
    try {
      if (!value)
        throw new Error('Failed to copy text to clipboard: value is null');
      navigator.clipboard.writeText(value);
      setTextCopiedToClipboard(true);
      setIsOpen(true);
      widgetCopyButtonIcon?.current?.blur();
    } catch (err) {
      console.error('Failed to copy text to clipboard: ', err);
    }
  };

  const iconSizes = {
    sm: 'h-2.5 w-2.5',
    md: 'h-4 w-4',
    lg: 'h-6 w-6',
  };

  const buttonSizes = {
    sm: 'h-7 w-7 min-w-0 p-0',
    md: 'h-8 w-8 min-w-0 p-0',
    lg: 'h-10 w-10 min-w-0 p-0',
  };

  return (
    <Tooltip
      content={textCopiedToClipboard ? 'Copied' : 'Copy'}
      isOpen={isOpen}
      closeDelay={250}
      placement="top"
    >
      <Button
        id="copy-button"
        isIconOnly
        onMouseOver={() => setIsOpen(true)}
        onMouseLeave={() =>
          !textCopiedToClipboard ? setIsOpen(false) : undefined
        }
        disableRipple
        data-testid="copy-button"
        className={cn(
          "bg-light-400 hover:bg-light-500 dark:bg-white/10 dark:hover:bg-white/20 text-dark dark:text-white rounded-md transition-colors duration-200 flex items-center justify-center min-w-0 p-0",
          buttonSizes[size],
          className
        )}
        radius="full"
        size="sm"
        aria-label="Copy"
        onBlur={onCopyWidgetBlur}
        onClick={(e) => {
          copyTextToClipboard();
          onClick?.(e);
        }}
        ref={widgetCopyButtonIcon}
      >
        <div className="flex items-center justify-center w-full h-full p-1.5">
          {textCopiedToClipboard ? (
            <CheckmarkIcon style={{background: 'transparent' }} className={iconSizes[size]} />
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className={iconSizes[size]}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
          )}
        </div>
      </Button>
    </Tooltip>
  );
};

export default CopyButton;
